import { request } from "./client";
import { ORGANISATION_BASE } from "./config";
import { tokenStore } from "@/auth/tokenStore";
import { authIdentity } from "@/utils/token";

const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface GetFileUrlResponse {
  url: string;
  key?: string;
  bucket?: string;
  expiresIn?: number; // Minutes, matching the organisation service contract.
  tenantScoped?: boolean;
}

/** Managed upload buckets — every object in these is company-scoped and needs exchange. */
const MANAGED_BUCKET_HOSTS = ["pw-cms-qa", "pw-cms-prod"];

/** Buckets that must never be routed through the resolver (public, server-owned assets). */
const PUBLIC_BUCKET_HOSTS = ["cms-asset-files", "pw-cms-web-assets"];

const cache = new Map<string, { value: string; expiresAt: number }>();

function isEditableDomain(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (PUBLIC_BUCKET_HOSTS.some((name) => host.startsWith(name))) {
    return false;
  }
  return MANAGED_BUCKET_HOSTS.some((name) => host.startsWith(name));
}

/**
 * Returns the raw S3 url unchanged when it is a public asset or not parseable,
 * so only objects living in the company-scoped upload buckets are exchanged.
 */
export function needsFileUrlExchange(url: string): boolean {
  try {
    return isEditableDomain(new URL(url).hostname);
  } catch {
    return false;
  }
}

function cacheKey(url: string): string {
  return `${authIdentity(tokenStore.get()) ?? "anonymous"}:${url}`;
}

function cachedS3Url(url: string): string | undefined {
  const key = cacheKey(url);
  const entry = cache.get(key);
  if (entry && entry.expiresAt > Date.now()) {
    return entry.value;
  }
  cache.delete(key);
  return undefined;
}

function storeS3Url(raw: string, resolved: string, expiresIn?: number): void {
  const serverTtl =
    typeof expiresIn === "number" && expiresIn > 0
      ? expiresIn * 60_000
      : CACHE_TTL_MS;
  cache.set(cacheKey(raw), {
    value: resolved,
    // Leave a small margin for request latency and opening the returned URL.
    expiresAt:
      Date.now() + Math.max(0, Math.min(serverTtl, CACHE_TTL_MS) - 30_000),
  });
}

/**
 * Exchanges a managed S3 URL for an authorized presigned GET URL.
 * Results are cached for the server's expiry, with a maximum of seven days
 * for legacy assets. Import and evidence links have a shorter lifetime.
 * Managed URLs fail closed when the tenant-scoped exchange is unavailable.
 */
export async function resolveFileUrl(raw: string): Promise<string> {
  if (!needsFileUrlExchange(raw)) {
    return raw;
  }
  const hit = cachedS3Url(raw);
  if (hit) {
    return hit;
  }
  const identity = authIdentity(tokenStore.get());
  const response = await request<GetFileUrlResponse>(
    "get",
    `${ORGANISATION_BASE}/file-url`,
    { params: { s3Url: raw } },
  );
  if (identity !== authIdentity(tokenStore.get())) {
    throw new Error(
      "The authenticated session changed while authorizing this file.",
    );
  }
  if (!response?.url) {
    throw new Error("The file could not be authorized for this account.");
  }
  const resolved = response.url;
  storeS3Url(raw, resolved, response?.expiresIn);
  return resolved;
}

/** Synchronous cache lookup (used to seed initial render state). */
export function peekResolvedFileUrl(raw: string): string | undefined {
  return needsFileUrlExchange(raw) ? cachedS3Url(raw) : undefined;
}

/**
 * Opens a company-scoped S3 object in a new tab for flows that cannot use
 * <img>/<a> (window.open, downloads). Resolves synchronously when cached and
 * refuses to open the raw managed URL when authorization cannot be confirmed.
 */
export async function openS3Url(url: string, target = "_blank"): Promise<void> {
  try {
    const resolved = await resolveFileUrl(url);
    window.open(resolved, target, "noopener,noreferrer");
  } catch (error) {
    throw new Error("Unable to open this file securely.", { cause: error });
  }
}
